import { MicroLabel } from '@codezo/quote-detail';
import type { OrderProgressRow, OrderProgressStep, OrderStat } from './types';

/**
 * The strip under the header: what the order is worth, and where each item is.
 *
 * One bordered box holding both, because they answer one question between them —
 * the numbers say how much, the matrix says how far. The flow spine below is the
 * detail; this is the glance.
 */
export function OrderOverview({
	stats,
	progress,
}: {
	stats: OrderStat[];
	/** Omitted when no item has a lifecycle — the box is then just the numbers. */
	progress?: OrderProgressRow[];
}) {
	return (
		<div className="overflow-hidden rounded-[var(--r-3)] border border-line-1 bg-surface">
			<div
				className="grid divide-x divide-y divide-line-1 sm:divide-y-0"
				style={{ gridTemplateColumns: `repeat(auto-fit, minmax(160px, 1fr))` }}
			>
				{stats.map((stat) => (
					<div key={stat.label} className="px-5 py-4">
						<MicroLabel className="mb-1.5 block">{stat.label}</MicroLabel>
						<div className="font-mono text-2xl font-medium whitespace-nowrap text-text-1">
							{stat.value}
						</div>
						{/* Always rendered, so a hintless cell keeps the same height as
						    its neighbours and the numbers stay on one baseline. */}
						<div className="mt-1 text-xs text-text-4">{stat.hint ?? ' '}</div>
					</div>
				))}
			</div>
			{progress && progress.length > 0 && <OrderProgressMatrix rows={progress} />}
		</div>
	);
}

/**
 * Progress per product, and per shipment under a product that ships in more than
 * one box.
 *
 * Columns come from the first row that has a lifecycle at all: it is nullable,
 * and one item without one shouldn't cost every other item its columns.
 */
/**
 * The steps a box can honestly speak for.
 *
 * The two steppers do NOT cover the same ground. A box is created, shipped and
 * delivered; it is never sampled, approved or paid for — its PRODUCT is. The
 * server builds a shipment's stepper positionally, marking everything left of
 * where the box has got to complete, so a shipped box comes back claiming
 * SAMPLING and APPROVAL on a product that skipped both, and PRODUCT PAYMENT on
 * one still unpaid.
 *
 * Rather than repaint those cells with the parent's answers — which would still
 * have a box reporting a sampling step that was never its to have — a shipment
 * row leaves them empty. A tick means "this happened"; blank means "not this
 * row's to say". Same rule the v1 matrix settled on, for the same reason.
 *
 * Ids, not the generated enum: this package stays structural and never imports
 * a schema. They are the `OrderItemLifecycleStepEnum` values.
 */
const SHIPMENT_OWN_STEPS = new Set(['DELIVERY', 'DELIVERED']);

export function OrderProgressMatrix({ rows }: { rows: OrderProgressRow[] }) {
	const columns = rows.find((r) => r.steps?.length)?.steps ?? [];
	if (columns.length === 0) return null;

	const anySplit = rows.some((r) => r.sub);
	// The label shares the header row's first column rather than sitting on a line
	// of its own, which is a whole row of vertical space in a box that is meant to
	// be glanced at. `Delivered qty` gets enough width to stay on one line — at
	// 84px it wrapped, and a two-line header dragged the stage labels off centre.
	const gridTemplateColumns = `minmax(120px, 1.3fr) repeat(${columns.length}, 1fr) 104px`;

	return (
		<div className="border-t border-line-1 bg-surface-sunken px-5 py-4">
			<div className="mb-3 grid items-baseline" style={{ gridTemplateColumns }}>
				<MicroLabel className="pr-3 tracking-[0.12em] text-text-3">
					Item progress
				</MicroLabel>
				{columns.map((column) => (
					<div
						key={column.step}
						className="text-center text-2xs font-medium uppercase tracking-[0.05em] text-text-4"
					>
						{column.label}
					</div>
				))}
				<div className="text-right text-2xs font-medium whitespace-nowrap uppercase tracking-[0.05em] text-text-4">
					Delivered qty
				</div>
			</div>

			{anySplit && (
				<div className="mb-2.5 text-2xs text-text-4">
					a row per shipment under any split product
				</div>
			)}

			{rows.map((row) => (
				<div
					key={row.key}
					className="grid items-center py-1.5"
					style={{ gridTemplateColumns }}
				>
					<div
						className={
							row.sub
								? 'truncate pr-3 pl-4 text-2xs text-text-4'
								: 'truncate pr-3 text-sm font-medium text-text-1'
						}
					>
						{row.label}
					</div>

					{row.steps ? (
						// Matched by step id rather than position, so a row whose
						// lifecycle is a different length can't slide out of its columns.
						columns.map((column, i) => {
							// Blank, not a hollow ring: an empty ring is a state —
							// "not reached yet" — and a box has not failed to be
							// sampled. See SHIPMENT_OWN_STEPS.
							if (row.sub && !SHIPMENT_OWN_STEPS.has(column.step)) {
								return <div key={column.step} />;
							}
							// `first` suppresses the connector running left. On a box
							// row that is its first OWN column, not column zero —
							// otherwise the line reaches back into cells it does not
							// occupy.
							const firstOwn =
								!!row.sub &&
								!columns.slice(0, i).some((c) => SHIPMENT_OWN_STEPS.has(c.step));
							return (
								<StepNode
									key={column.step}
									step={row.steps!.find((s) => s.step === column.step)}
									first={i === 0 || firstOwn}
									muted={!!row.sub}
								/>
							);
						})
					) : (
						<div
							className="text-center text-2xs text-text-4"
							style={{ gridColumn: `span ${columns.length}` }}
						>
							Progress unavailable
						</div>
					)}

					<div className="text-right font-mono text-2xs">
						{row.quantity ? (
							<span
								className={
									row.quantity.delivered > 0 ? 'text-accent' : 'text-text-4'
								}
							>
								{row.quantity.delivered.toLocaleString()}/
								{row.quantity.total.toLocaleString()}
							</span>
						) : (
							<span className="text-text-4">{row.sub ? '' : '—'}</span>
						)}
					</div>
				</div>
			))}
		</div>
	);
}

/**
 * The mark for a step that never applied — a slashed ring.
 *
 * It keeps the ring, so the row still scans as one sequence of nodes and the
 * columns stay on their centres. What changes is what is inside it: a slash, the
 * conventional "not applicable" glyph, which at 12px is legible where a
 * difference in opacity is not.
 *
 * Two earlier attempts were worse, and both failed the same way — by reading as
 * an ABSENCE rather than a statement. A fainter ring said "not yet" (the one
 * meaning it must never have). A bare dash with no ring read as a gap in the
 * connector, as though the line had failed to draw.
 *
 * Opaque ground, so the connector appears to stop at the mark rather than run
 * through it: the path visibly does not pass through a step that never happened.
 */
function SkippedMark({ size, title }: { size: number; title?: string }) {
	return (
		<span
			title={title}
			className="relative z-10 flex shrink-0 items-center justify-center rounded-full"
			style={{ height: size, width: size, background: 'var(--surface-sunken)' }}
		>
			{/* SVG, not a rotated div. A 1.5px box rotated 45° lands on no pixel
			    boundary at either end and antialiases into a smudge at this size —
			    which is what made the first slash read as a rendering artifact rather
			    than a glyph. Drawn on a 12-unit grid the stroke stays crisp at any
			    size the matrix uses. */}
			<svg
				width={size}
				height={size}
				viewBox="0 0 12 12"
				fill="none"
				className="block"
				aria-hidden
			>
				<circle cx="6" cy="6" r="5.25" stroke="var(--line-3)" strokeWidth="1.5" />
				{/* Stops just inside the ring rather than crossing it: a slash running
				    over the stroke doubles the ink at both ends and reads as a blot. */}
				<line
					x1="3.6"
					y1="8.4"
					x2="8.4"
					y2="3.6"
					stroke="var(--text-4)"
					strokeWidth="1.5"
					strokeLinecap="round"
				/>
			</svg>
		</span>
	);
}

/** One node plus the line back to the previous one. */
function StepNode({
	step,
	first,
	muted,
}: {
	step: OrderProgressStep | undefined;
	first: boolean;
	muted: boolean;
}) {
	// A skipped step never reads as done, and beats both other states: the server
	// means "this never applied here", which is not a position on the path at all.
	const skipped = !!step?.skipped;
	const completed = !skipped && !!step?.completed;
	const current = !skipped && !!step?.current;
	// Overtaken: reached, never done, and the item has moved on. It must not wear
	// a tick — the whole reason the backend stopped lending `completed` to it —
	// and it is not "not yet reached" either, so the ring takes the warn tone.
	const outstanding = !skipped && !completed && !current && !!step?.outstanding;
	// Every inner size divides evenly into its container, so the dot and the tick
	// land on whole pixels. Halve one of these and the dot sits visibly off-centre.
	const size = muted ? 10 : 12;
	const dot = 4;
	const tick = size - 4;
	const ring = outstanding
		? 'var(--status-warn)'
		: completed || current
			? 'var(--accent)'
			: 'var(--line-2)';

	return (
		<div className="relative flex items-center justify-center">
			{!first && (
				<div
					className="absolute h-[1.5px] rounded-[1px]"
					style={{
						left: `calc(-50% + ${size * 0.8}px)`,
						right: `calc(50% + ${size * 0.8}px)`,
						top: '50%',
						transform: 'translateY(-50%)',
						background: completed ? 'var(--accent-line)' : 'var(--line-2)',
					}}
				/>
			)}
			{skipped ? (
				<SkippedMark
					size={size}
					// Names the step, because the reader is looking at a column and
					// needs to know it is THIS step that doesn't apply — not the
					// item's sampling as a whole, which may well be under way.
					title={`${step?.label ?? 'This step'} never applied to this item`}
				/>
			) : (
				<div
					className="relative z-10 flex shrink-0 items-center justify-center rounded-full"
					// Named for the same reason the skipped mark is: the reader is
					// looking at a column of rings and has to know which step the warn
					// one belongs to.
					title={
						outstanding
							? (step?.outstandingReason ??
								`${step?.label ?? 'This step'} was never completed`)
							: undefined
					}
					style={{
						height: size,
						width: size,
						opacity: muted ? 0.85 : 1,
						background: completed
							? 'var(--accent)'
							: current || outstanding
								? 'transparent'
								: 'var(--bg-3)',
						// The outline is an INSET shadow, not a border: a border eats into
						// the content box (12px − 2 × 1.5px = 9px), leaving 2.5px either side
						// of a 4px dot, which the browser rounds unevenly. A shadow keeps the
						// content box the full size.
						boxShadow: current
							? `inset 0 0 0 1.5px ${ring}, 0 0 0 ${muted ? 2 : 3}px var(--accent-soft)`
							: `inset 0 0 0 1.5px ${ring}`,
					}}
				>
					{completed && (
						<svg
							width={tick}
							height={tick}
							viewBox="0 0 12 12"
							fill="none"
							className="block"
						>
							<path
								d="M2.5 5.9l2.4 2.4 4.6-4.6"
								stroke="var(--accent-fg)"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
					)}
					{current && (
						<span
							className="block rounded-full"
							style={{ height: dot, width: dot, background: 'var(--accent)' }}
						/>
					)}
				</div>
			)}
		</div>
	);
}

/* ────────────────────────── building the rows ────────────────────────── */

/**
 * The minimum an item has to look like to be laid out. Structural on purpose:
 * each app passes its own generated GraphQL objects straight in, extra fields
 * and all, and the package never imports a schema.
 */
export interface ProgressItemInput {
	id: string;
	productName?: string | null;
	quantity: number;
	deliveredQuantity?: number | null;
	lifecycle?: OrderProgressStep[] | null;
}

export interface ProgressShipmentInput {
	id: string;
	sequence: number;
	lifecycle?: OrderProgressStep[] | null;
	/**
	 * When the box actually arrived; null until it has. The schema is explicit
	 * that this is the only date that means arrival — see `shipmentSteps`, which
	 * is the one place this package lets a fact overrule the server's stepper.
	 */
	actualDeliveryDate?: string | null;
	lines: { orderItemId: string; quantity: number }[];
}

/**
 * A box's own two steps, reconciled with whether it actually arrived.
 *
 * The server builds a shipment's stepper positionally — where the box has got
 * to — and its last step does not track arrival: a delivered box comes back with
 * DELIVERED still merely `current`, so a fully received product reads as one
 * whose boxes are all still in transit.
 *
 * `actualDeliveryDate` is the fact that settles it, and it settles it both ways:
 * arrived means DELIVERY and DELIVERED are done, not-arrived means DELIVERED is
 * not done however the stepper positioned the marker. This is deliberately the
 * ONLY derivation in this file, and it reads one documented date rather than
 * re-deriving a lifecycle from a status — the thing that once rendered a
 * PARTIALLY_SHIPPED item two steps backwards.
 */
function shipmentSteps(shipment: ProgressShipmentInput): OrderProgressStep[] | null {
	const steps = shipment.lifecycle;
	if (!steps?.length) return steps ?? null;

	const arrived = shipment.actualDeliveryDate != null;
	const shipped = arrived || !!steps.find((s) => s.step === 'DELIVERY')?.completed;

	return steps.map((step) => {
		if (step.step === 'DELIVERY' && arrived) {
			// Nothing arrives without having left.
			return { ...step, completed: true, current: false, outstanding: false };
		}
		if (step.step !== 'DELIVERED') return step;
		return arrived
			? { ...step, completed: true, current: false, outstanding: false }
			: // In transit reads as the step in progress; still in production
				// reads as not reached — which is what `shipped` separates.
				{ ...step, completed: false, current: step.current || shipped };
	});
}

/**
 * Turn items and shipments into matrix rows.
 *
 * Lives here rather than in either app because the split-shipment rule is the
 * subtle part and two copies of it would drift: a product carried by two or more
 * boxes gets a sub-row per box, one carried by exactly one gets none — that row
 * would only repeat what the product row already said.
 */
export function buildProgressRows({
	items,
	shipments,
}: {
	items: ProgressItemInput[];
	shipments: ProgressShipmentInput[];
}): OrderProgressRow[] {
	const shipmentsByItem = new Map<string, ProgressShipmentInput[]>();
	for (const shipment of shipments) {
		for (const line of shipment.lines) {
			const list = shipmentsByItem.get(line.orderItemId);
			if (list) {
				// A shipment can carry the same item on more than one line; the box
				// is still one row.
				if (!list.includes(shipment)) list.push(shipment);
			} else {
				shipmentsByItem.set(line.orderItemId, [shipment]);
			}
		}
	}

	const rows: OrderProgressRow[] = [];
	for (const item of items) {
		rows.push({
			key: item.id,
			label: item.productName ?? 'Product',
			steps: item.lifecycle ?? null,
			// Null means "not computable", not zero — so show nothing rather than
			// claim none of it arrived.
			quantity:
				item.deliveredQuantity == null
					? null
					: { delivered: item.deliveredQuantity, total: item.quantity },
		});

		const carrying = (shipmentsByItem.get(item.id) ?? []).sort(
			(a, b) => a.sequence - b.sequence
		);
		if (carrying.length < 2) continue;

		for (const shipment of carrying) {
			const inThisBox = shipment.lines
				.filter((l) => l.orderItemId === item.id)
				.reduce((sum, l) => sum + l.quantity, 0);
			// The same box appears under every product it carries, with the same
			// stepper each time. Say so, or three products in one box reads as three
			// separate boxes.
			const productsInBox = new Set(shipment.lines.map((l) => l.orderItemId)).size;
			rows.push({
				key: `${item.id}:${shipment.id}`,
				label:
					`Shipment ${shipment.sequence} · ${inThisBox.toLocaleString()}/${item.quantity.toLocaleString()}` +
					(productsInBox > 1 ? ' · shared box' : ''),
				sub: true,
				steps: shipmentSteps(shipment),
				quantity: null,
			});
		}
	}

	return rows;
}
